import React from 'react';
import {FormProvider, useForm, useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import moment from 'moment';
import {filter, map, sumBy} from 'lodash';
import {usePaymentSettings} from '../../../hooks';
import {useComputedReservationDetails} from '../../../context/computedReservationDetails';
import {UpsellingOrderFormProps} from './types';
import {PriceItem} from '../../../utils/upselling/types';
import {
  getOfferStatus,
  OFFER_PRICE_TYPES,
  UNIT_PRICE_TYPES,
} from '../../../utils/upselling';
import ButtonsBlock from '../shared/ButtonsBlock';
import SinglePriceForm from './SinglePriceForm';
import MultiPriceForm from './MultiPriceForm';
import {OrderForm, Row, TotalPriceRow} from './styled';

export enum FORM_NAMES {
  numberOfPeople = 'number_of_people',
  quantity = 'quantity',
  time = 'time',
  date = 'date',
  items = 'items',
}

export enum ItemsFieldsNames {
  id = 'id',
  quantity = 'quantity',
  name = 'name',
  price = 'price',
  unit_type = 'unit_type',
  checked = 'checked',
}

export type ItemsFields = {
  [ItemsFieldsNames.id]: string;
  [ItemsFieldsNames.quantity]: number;
  [ItemsFieldsNames.price]: string | number;
  [ItemsFieldsNames.unit_type]: UNIT_PRICE_TYPES;
  [ItemsFieldsNames.name]?: string;
  [ItemsFieldsNames.checked]?: boolean;
};

export type FormTypes = {
  [FORM_NAMES.numberOfPeople]: string;
  [FORM_NAMES.quantity]: string | number;
  [FORM_NAMES.items]: ItemsFields[];
  [FORM_NAMES.time]: {label: string; value: string};
  [FORM_NAMES.date]: string;
};

function UpsellingOrderForm({
  offerData,
  offerId,
  handleSubmitForm,
  guest,
  disabled,
  onBookAndKeepExploring,
  enterGuestMail,
}: UpsellingOrderFormProps) {
  const {t} = useTranslation();
  const {reservation} = useComputedReservationDetails();
  const {paymentSettingsCurrencySign: currency} = usePaymentSettings();
  const methods = useForm<FormTypes>({
    mode: 'onSubmit',
    shouldUnregister: false,
  });

  const {control, handleSubmit, reset} = methods;
  const itemsWatch = useWatch({control, name: FORM_NAMES.items});
  const totalPrice = sumBy(
    itemsWatch,
    (item: PriceItem) => Number(item.price) * Number(item.quantity),
  );

  const priceForms = React.useMemo<{
    [key in OFFER_PRICE_TYPES]: JSX.Element;
  }>(() => {
    return {
      [OFFER_PRICE_TYPES.single]: (
        <SinglePriceForm disabled={disabled} offerData={offerData} />
      ),
      [OFFER_PRICE_TYPES.multiple]: (
        <MultiPriceForm disabled={disabled} offerData={offerData} />
      ),
    };
  }, [disabled, offerData]);

  const PriceForm = React.useCallback(
    () => priceForms[offerData.price_type],
    [priceForms, offerData.price_type],
  );

  const buildDealPayload = (data: FormTypes) => {
    const requested_items = map(
      filter(data.items, o => o.checked || o.quantity > 0),
      ({id, quantity}) => ({
        item: id,
        quantity,
      }),
    );

    const status = getOfferStatus(offerData.confirmation_type);
    const leaderId = reservation?.guest_group?.leader_id;
    const leaderName: string = filter(reservation?.guest_group?.members, [
      'id',
      leaderId,
    ])[0]?.full_name;
    const guestId = guest?.id;
    const guestName = guest?.full_name;
    const guestEmail = guest?.email || enterGuestMail;
    const requestedDate = data[FORM_NAMES.date]
      ? moment(data[FORM_NAMES.date]).format('YYYY-MM-DD')
      : undefined;
    const requestedTime = data[FORM_NAMES.time]?.value as string;
    const requestedFor =
      requestedDate && requestedTime ? `${requestedDate}T${requestedTime}` : undefined;
    // const numberOfPeople = data[FormNames.numberOfPeople]
    //   ? Number(data[FormNames.numberOfPeople])
    //   : undefined;

    return {
      core_guest_id: guestId || leaderId,
      core_guest_name: guestName || leaderName,
      guest_email: guestEmail,
      core_housing_id: reservation?.housing?.id,
      core_housing_name: reservation?.housing?.name,
      core_reservation_id: reservation?.id,
      offer_id: offerId,
      quantity: 1,
      requested_items,
      status,
      requested_for: requestedFor,
      // number_of_people: numberOfPeople,
      // quantity: 1,

      // name: offerData.title,
      // price: totalPrice,
      // from,
      // to,
      // time: time.value,
      offerName: offerData.title,
    };
  };

  const onSubmit = (data: FormTypes) => {
    const payload = buildDealPayload(data);
    handleSubmitForm(payload, reset);
  };

  const handleBookAndKeepExploring = (data: FormTypes) => {
    const payload = buildDealPayload(data);
    onBookAndKeepExploring(payload);
  };

  return (
    <form id="singlePriceForm" className="form" onSubmit={handleSubmit(onSubmit)}>
      <FormProvider {...methods}>
        <OrderForm>
          <PriceForm />
          {/*<AdditionalFieldsForm category={offerData.category} />*/}
          <TotalPriceRow>
            <div className="rowName">{t('total_price')}</div>
            <div className="priceValue">{`${totalPrice}${currency}`}</div>
          </TotalPriceRow>

          <Row style={{justifyContent: 'center', marginTop: '50px'}}>
            <ButtonsBlock
              formName="singlePriceForm"
              disabled={totalPrice <= 0 || disabled}
              confirmationType={offerData.confirmation_type}
              handleBookAndKeepExploring={handleSubmit(handleBookAndKeepExploring)}
            />
          </Row>
        </OrderForm>
      </FormProvider>
    </form>
  );
}

export default React.memo(UpsellingOrderForm);
